import { AttachmentSection } from '@components/Attachments'
import { countLabel } from '@/utils/plural'
import { DateTimePicker } from '@components/DateTimePicker'
import LocationAutocomplete, { PlaceSuggestion } from '@components/LocationAutocomplete'
import Modal from '@components/Modal'
import { useAuth } from '@context/AuthContext'
import { UpdateAccommodationPayload } from '@hooks/useUpdateAccommodation'
import { inputToUtcString, utcStringToInput } from '@lib/datetime'
import { GetAccommodationResponse, GetUserType } from '@orbit/api-types'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
    AddLinkButton,
    Button,
    ButtonGroup,
    CompactFormLabel,
    DateInputGroup,
    DateInputRow,
    FormDescription,
    FormGroup,
    FormInput,
    FormLabel,
    FormTextArea,
    GuestCheckbox,
    GuestError,
    GuestName,
    GuestRow,
    GuestSummary,
    GuestsList,
    LinkInput,
    LinkRow,
    LinksSection,
    LocationSecondaryText,
    RemoveLinkButton,
} from '../AddAccommodationModal/AddAccommodationModal.styles'
import { backgroundOrange } from '@/theme/colors'
import { Avatar } from '@components/Avatar'

const MAX_LINKS = 3

interface EditAccommodationModalProps {
    isOpen: boolean
    onClose: () => void
    onSubmit: (data: UpdateAccommodationPayload) => void
    onDelete: () => void
    accommodation: GetAccommodationResponse
    members: GetUserType[]
    isSubmitting?: boolean
}

const EditAccommodationModal: React.FC<EditAccommodationModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    onDelete,
    accommodation,
    members,
    isSubmitting = false,
}) => {
    const { user } = useAuth()
    const { tripId } = useParams<{ tripId: string }>()

    const [name, setName] = useState('')
    const [locationPrimaryText, setLocationPrimaryText] = useState('')
    const [selectedPlace, setSelectedPlace] = useState<PlaceSuggestion | null>(null)
    const [checkInDate, setCheckInDate] = useState('')
    const [checkOutDate, setCheckOutDate] = useState('')
    const [notes, setNotes] = useState('')
    const [links, setLinks] = useState<string[]>([])
    const [selectedGuests, setSelectedGuests] = useState<Set<number>>(new Set())

    useEffect(() => {
        if (isOpen && accommodation) {
            setName(accommodation.name)
            if (accommodation.location) {
                setLocationPrimaryText(accommodation.location.fullText || accommodation.location.primaryText || '')
                setSelectedPlace(accommodation.location.googlePlaceId ? {
                    placeId: accommodation.location.googlePlaceId,
                    primaryText: accommodation.location.primaryText || '',
                    secondaryText: accommodation.location.secondaryText || '',
                    fullText: accommodation.location.fullText,
                    types: accommodation.location.googleTypes || [],
                } : null)
            } else {
                setLocationPrimaryText('')
                setSelectedPlace(null)
            }
            setCheckInDate(utcStringToInput(accommodation.checkInDate))
            setCheckOutDate(utcStringToInput(accommodation.checkOutDate))
            setNotes(accommodation.notes ?? '')
            setLinks(accommodation.links ?? [])
            setSelectedGuests(new Set(accommodation.guests.map(g => g.userId)))
        }
    }, [isOpen, accommodation])

    const handleLocationPrimaryTextChange = (value: string) => {
        setLocationPrimaryText(value)
        setSelectedPlace(null)
    }

    const handleLocationSelect = (place: PlaceSuggestion) => {
        setSelectedPlace(place)
    }

    const toggleGuest = (userId: number) => {
        setSelectedGuests(prev => {
            const next = new Set(prev)
            if (next.has(userId)) {
                next.delete(userId)
            } else {
                next.add(userId)
            }
            return next
        })
    }

    const addLink = () => {
        if (links.length < MAX_LINKS) {
            setLinks(prev => [...prev, ''])
        }
    }

    const updateLink = (index: number, value: string) => {
        setLinks(prev => prev.map((l, i) => (i === index ? value : l)))
    }

    const removeLink = (index: number) => {
        setLinks(prev => prev.filter((_, i) => i !== index))
    }

    const canSubmit =
        name.trim().length > 0 &&
        checkInDate.length > 0 &&
        checkOutDate.length > 0 &&
        selectedGuests.size > 0 &&
        new Date(checkInDate) < new Date(checkOutDate) &&
        !isSubmitting

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!canSubmit) return

        const filteredLinks = links.filter(l => l.trim().length > 0)

        const location = selectedPlace
            ? {
                fullText: selectedPlace.fullText,
                primaryText: selectedPlace.primaryText,
                secondaryText: selectedPlace.secondaryText || undefined,
                googlePlaceId: selectedPlace.placeId,
                googleTypes: selectedPlace.types,
            }
            : locationPrimaryText.trim()
                ? {
                    fullText: locationPrimaryText.trim(),
                    primaryText: locationPrimaryText.trim(),
                    googleTypes: [],
                }
                : null

        const payload: UpdateAccommodationPayload = {
            name: name.trim(),
            checkInDate: inputToUtcString(checkInDate),
            checkOutDate: inputToUtcString(checkOutDate),
            guestUserIds: Array.from(selectedGuests),
            notes: notes.trim() || null,
            links: filteredLinks,
            location,
        }

        onSubmit(payload)
    }


    const getMemberName = (member: GetUserType) => {
        const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ')
        return member.id === user?.id ? `${fullName} (You)` : fullName
    }

    const sortedMembers = [...members].sort((a, b) => {
        if (a.id === user?.id) return -1
        if (b.id === user?.id) return 1
        return 0
    })

    const dateError = checkInDate && checkOutDate && new Date(checkInDate) >= new Date(checkOutDate)
        ? 'Check-out must be after check-in'
        : null

    const formId = 'edit-accommodation-form'

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Edit Accommodation"
            size="large"
            backgroundColor={backgroundOrange}
            closeOnBackdropClick={false}
            onDelete={onDelete}
            footer={
                <ButtonGroup>
                    <Button type="button" variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" form={formId} variant="primary" disabled={!canSubmit}>
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </Button>
                </ButtonGroup>
            }
        >
            <form id={formId} onSubmit={handleSubmit}>
                <FormGroup>
                    <FormLabel htmlFor="edit-accommodation-location">Location (Optional)</FormLabel>
                    <LocationAutocomplete
                        id="edit-accommodation-location"
                        value={locationPrimaryText}
                        onChange={handleLocationPrimaryTextChange}
                        onPlaceSelect={handleLocationSelect}
                        placeholder="Search for a location..."
                    />
                    {selectedPlace?.secondaryText && (
                        <LocationSecondaryText>
                            {selectedPlace.secondaryText}
                        </LocationSecondaryText>
                    )}
                </FormGroup>

                <FormGroup>
                    <FormLabel htmlFor="edit-accommodation-name">Name</FormLabel>
                    <FormInput
                        id="edit-accommodation-name"
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="e.g. Marriott Downtown"
                        maxLength={75}
                    />
                </FormGroup>

                <FormGroup>
                    <FormLabel>Dates</FormLabel>
                    <DateInputRow>
                        <DateInputGroup>
                            <CompactFormLabel htmlFor="edit-checkin-date">Check-in</CompactFormLabel>
                            <DateTimePicker
                                id="edit-checkin-date"
                                value={checkInDate}
                                onChange={next => {
                                    setCheckInDate(next)
                                    if (checkOutDate && next && next >= checkOutDate) {
                                        setCheckOutDate('')
                                    }
                                }}
                                placeholder="Select check-in"
                            />
                        </DateInputGroup>
                        <DateInputGroup>
                            <CompactFormLabel htmlFor="edit-checkout-date">Check-out</CompactFormLabel>
                            <DateTimePicker
                                id="edit-checkout-date"
                                value={checkOutDate}
                                onChange={next => {
                                    setCheckOutDate(next)
                                    if (checkInDate && next && next <= checkInDate) {
                                        setCheckInDate('')
                                    }
                                }}
                                placeholder="Select check-out"
                            />
                        </DateInputGroup>
                    </DateInputRow>
                    {dateError && (
                        <GuestSummary>
                            <GuestError>{dateError}</GuestError>
                        </GuestSummary>
                    )}
                </FormGroup>

                <FormGroup>
                    <FormLabel>Guests</FormLabel>
                    <FormDescription>Select who is staying at this accommodation</FormDescription>
                    <GuestsList>
                        {sortedMembers.map(member => {
                            const isSelected = selectedGuests.has(member.id)
                            return (
                                <GuestRow
                                    key={member.id}
                                    $isSelected={isSelected}
                                    onClick={() => toggleGuest(member.id)}
                                >
                                    <GuestCheckbox $checked={isSelected} />
                                    <Avatar user={member} size="md" />
                                    <GuestName>{getMemberName(member)}</GuestName>
                                </GuestRow>
                            )
                        })}
                    </GuestsList>
                    <GuestSummary>
                        {selectedGuests.size === 0 ? (
                            <GuestError>Select at least one guest</GuestError>
                        ) : (
                            `${countLabel(selectedGuests.size, 'guest')} selected`
                        )}
                    </GuestSummary>
                </FormGroup>

                <FormGroup>
                    <FormLabel>Links</FormLabel>
                    <FormDescription>Add booking confirmations or relevant URLs (max {MAX_LINKS})</FormDescription>
                    <LinksSection>
                        {links.map((link, index) => (
                            <LinkRow key={index}>
                                <LinkInput
                                    type="text"
                                    value={link}
                                    onChange={e => updateLink(index, e.target.value)}
                                    placeholder="e.g. https://booking.com/..."
                                />
                                <RemoveLinkButton type="button" onClick={() => removeLink(index)}>
                                    ✕
                                </RemoveLinkButton>
                            </LinkRow>
                        ))}
                        <AddLinkButton
                            type="button"
                            onClick={addLink}
                            disabled={links.length >= MAX_LINKS}
                        >
                            + Add Link
                        </AddLinkButton>
                    </LinksSection>
                </FormGroup>

                <FormGroup>
                    <FormLabel htmlFor="edit-accommodation-notes">Notes</FormLabel>
                    <FormDescription>Add any relevant notes about the accommodation</FormDescription>
                    <FormTextArea
                        id="edit-accommodation-notes"
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        placeholder="e.g. Check-in code is 1234, parking is in garage B..."
                    />
                </FormGroup>

                {tripId && (
                    <AttachmentSection
                        tripId={tripId}
                        resourceType="ACCOMMODATION"
                        resourceId={accommodation.id}
                        fileIds={accommodation.fileIds || []}
                    />
                )}
            </form>
        </Modal>
    )
}

export default EditAccommodationModal
